import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ApiError } from './lib/api';
import './styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Balances change on-chain, not on focus. Refetching on every window
      // focus would hammer public providers for no benefit.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Never retry an auth failure or a locked vault — retrying cannot fix
        // either, and retrying a bad password burns lockout attempts.
        if (error instanceof ApiError && (error.isAuth || error.isLocked || error.status < 500)) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root');

// Anything that escapes React's render tree (event-handler errors, async
// rejections) would otherwise blank the page silently. Surface it.
function showFatal(message: string): void {
  if (document.getElementById('gauntlet-fatal')) return;
  const overlay = document.createElement('div');
  overlay.id = 'gauntlet-fatal';
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;' +
    'background:#0a0a0a;color:#fafafa;font-family:ui-sans-serif,system-ui,sans-serif;padding:16px;';
  const card = document.createElement('div');
  card.style.cssText = 'max-width:560px;border:1px solid #7f1d1d;border-radius:12px;background:#1c1917;padding:16px;';
  card.innerHTML =
    '<p style="font-weight:600;margin:0 0 6px;">Gauntlet crashed</p>' +
    '<pre style="white-space:pre-wrap;word-break:break-all;font-size:12px;margin:0 0 12px;">' +
    message.replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' })[c] ?? c) +
    '</pre>';
  const button = document.createElement('button');
  button.textContent = 'Reload';
  button.style.cssText = 'background:#a16207;border:0;border-radius:8px;padding:8px 16px;color:#0a0a0a;font-weight:600;cursor:pointer;';
  button.onclick = () => window.location.reload();
  card.appendChild(button);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
}
window.addEventListener('error', (event) => showFatal(event.message ?? 'Unknown error'));
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason instanceof Error ? event.reason.message : String(event.reason);
  showFatal('Unhandled rejection: ' + reason);
});

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
